/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { ReplaySubject } from 'rxjs';
import { PlatformDetectorService } from '../platform-detector/platform-detector.service';

export interface RetryAttemptTelemetryData {
    isSuccess: boolean;
    fileName: string;
    attemptCount: number;
    error: any;
}

export class RetryChunkLoad {
    static retrySubject = new ReplaySubject<RetryAttemptTelemetryData>(5);

    static retryAttempts = 5;

    static intervalBetweenRetries = 50;

    static sendTelemetry(logger) {
        RetryChunkLoad.retrySubject.subscribe({
            next: (telemetryData: RetryAttemptTelemetryData) => {
                if (telemetryData.isSuccess) {
                    // Send HTTPSuccess event
                    logger.info('success event ', Date.now(), telemetryData);
                } else {
                    // Send HTTPFailure Data
                    logger.info('failure event ', Date.now(), telemetryData);
                }
            }
        });
    }

    static retry(
        fn,
        module_name,
        retriesLeft = RetryChunkLoad.retryAttempts,
        interval = RetryChunkLoad.intervalBetweenRetries
    ) {
        let telemetryData: RetryAttemptTelemetryData;

        return new Promise((resolve, reject) => {
            fn()
                .then(function (data) {
                    if (PlatformDetectorService.isBrowserPlatform()) {
                        telemetryData = {
                            isSuccess: true,
                            attemptCount: RetryChunkLoad.retryAttempts + 1 - retriesLeft,
                            error: null,
                            fileName: module_name
                        };
                        RetryChunkLoad.retrySubject.next(telemetryData);
                    }
                    return resolve(data);
                })
                .catch(error => {
                    if (!PlatformDetectorService.isBrowserPlatform()) {
                        reject(error);
                    } else {
                        telemetryData = {
                            isSuccess: false,
                            attemptCount: RetryChunkLoad.retryAttempts + 1 - retriesLeft,
                            error,
                            fileName: module_name
                        };
                        RetryChunkLoad.retrySubject.next(telemetryData);
                        if (retriesLeft === 1) {
                            reject(error);
                            return;
                        }
                        setTimeout(() => {
                            RetryChunkLoad.retry(
                                fn,
                                module_name,
                                retriesLeft - 1,
                                RetryChunkLoad.intervalBetweenRetries * (RetryChunkLoad.retryAttempts + 1 - retriesLeft)
                            ).then(resolve, reject);
                        }, interval);
                    }
                });
        });
    }
}
